import {CommonModule, DatePipe} from '@angular/common';
import {Component, computed, inject, signal} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {firstValueFrom} from 'rxjs';
import {Button} from 'primeng/button';
import {MultiSelectModule} from 'primeng/multiselect';
import {Select} from 'primeng/select';
import {TableModule} from 'primeng/table';
import {DialogModule} from 'primeng/dialog';
import {InputTextModule} from 'primeng/inputtext';
import {TextareaModule} from 'primeng/textarea';
import {BackgroundCardComponent} from '../../../shared/components/titled-background-card/background-card.component';
import {LoadingComponent} from '../../../shared/components/loading/loading.component';
import {NoDataComponent} from '../../../shared/components/no-data/no-data.component';
import {ToastService} from '@frontend/toast';
import {
  DemonstrationAgenda,
  DirectusService,
  ModuleFunction,
  SubcriteriaAttributeDirectus,
  UseCasePersona,
} from '@frontend/directus';
import {
  GeneratedUseCaseAdminRow,
  GeneratedUseCaseUpdateRequest,
  SelecthorAdminUseCasesService,
  UseCaseGenerationRunAdminRow,
  UseCaseGenerationRunStatusResponse,
} from './selecthor-admin-use-cases.service';
import {TooltipModule} from 'primeng/tooltip';

interface UseCaseColumn {
  field: string;
  header: string;
}

interface ModuleFunctionOption {
  label: string;
  value: number;
}

@Component({
  selector: 'app-selecthor-admin-use-cases',
  templateUrl: './selecthor-admin-use-cases.component.html',
  imports: [
    CommonModule,
    FormsModule,
    DatePipe,
    TableModule,
    Button,
    Select,
    MultiSelectModule,
    DialogModule,
    InputTextModule,
    TextareaModule,
    TooltipModule,
    BackgroundCardComponent,
    LoadingComponent,
    NoDataComponent,
  ],
})
export class SelecthorAdminUseCasesComponent {
  private readonly useCasesService = inject(SelecthorAdminUseCasesService);
  private readonly toast = inject(ToastService);
  private readonly directusService = inject(DirectusService);

  readonly loading = signal(false);
  readonly runLoading = signal(false);
  readonly rows = signal<GeneratedUseCaseAdminRow[]>([]);
  readonly runRows = signal<UseCaseGenerationRunAdminRow[]>([]);
  readonly showRunsView = signal(false);
  readonly expandedRows = signal<Record<string, boolean>>({});
  readonly generationDialogVisible = signal(false);
  readonly generationRunning = signal(false);
  readonly generationRunningTooltip = signal<string>('');
  readonly attributeDialogVisible = signal(false);
  readonly generationModuleFunctions = signal<ModuleFunctionOption[]>([]);
  readonly selectedGenerationModuleFunctionIds = signal<number[]>([]);
  readonly enabledUpdateLoadingById = signal<Record<number, boolean>>({});
  readonly agendaItemLabels = signal<Record<number, string>>({});
  readonly moduleFunctionLabels = signal<Record<number, string>>({});
  readonly personaLabels = signal<Record<string, string>>({});
  readonly attributeLabels = signal<Record<string, string>>({});
  readonly selectedAttributeNames = signal<string[]>([]);
  readonly selectedAttributeDialogTitle = signal<string>('');
  readonly runDetailsDialogVisible = signal(false);
  readonly runDetailsDialogTitle = signal('');
  readonly runDetailsItems = signal<string[]>([]);
  readonly editDialogVisible = signal(false);
  readonly editingUseCaseId = signal<number | null>(null);
  readonly editingTitleDe = signal('');
  readonly editingDescriptionDe = signal('');
  readonly editingDurationMin = signal<number>(1);
  readonly editSaving = signal(false);
  readonly generateTitleLoading = signal(false);
  readonly generateDescriptionLoading = signal(false);

  readonly selectedAgendaItem = signal<number | null>(null);
  readonly selectedModuleFunction = signal<number | null>(null);
  readonly selectedPersona = signal<string | null>(null);

  readonly allColumns: UseCaseColumn[] = [
    {field: 'id', header: 'ID'},
    {field: 'runId', header: 'Run'},
    {field: 'agendaItemId', header: 'Agenda Item'},
    {field: 'moduleFunctionId', header: 'Module Function'},
    {field: 'personaId', header: 'Persona'},
    {field: 'titleDe', header: 'Title (DE)'},
    {field: 'durationMin', header: 'Duration'},
    {field: 'subcriteriaAttributeIds', header: 'Attributes'},
    {field: 'createdAt', header: 'Created At'},
    {field: 'enabled', header: 'Enabled'},
    {field: 'edit', header: 'Edit'},
  ];
  readonly selectedColumns = signal<UseCaseColumn[]>([...this.allColumns]);

  readonly agendaItemOptions = computed(() =>
    this.rows()
      .map((row) => row.agendaItemId)
      .filter((value, index, values) => values.indexOf(value) === index)
      .sort((a, b) => this.getAgendaItemLabel(a).localeCompare(this.getAgendaItemLabel(b)))
      .map((value) => ({label: this.getAgendaItemLabel(value), value})),
  );

  readonly moduleFunctionOptions = computed(() =>
    this.rows()
      .map((row) => row.moduleFunctionId)
      .filter((value, index, values) => values.indexOf(value) === index)
      .sort((a, b) => this.getModuleFunctionLabel(a).localeCompare(this.getModuleFunctionLabel(b)))
      .map((value) => ({label: this.getModuleFunctionLabel(value), value})),
  );

  readonly personaOptions = computed(() =>
    this.rows()
      .map((row) => row.personaId)
      .filter((value, index, values) => values.indexOf(value) === index)
      .sort((a, b) => this.getPersonaLabel(a).localeCompare(this.getPersonaLabel(b)))
      .map((value) => ({label: this.getPersonaLabel(value), value})),
  );

  readonly filteredRows = computed(() =>
    this.rows().filter(
      (row) =>
        (this.selectedAgendaItem() == null || row.agendaItemId === this.selectedAgendaItem()) &&
        (this.selectedModuleFunction() == null ||
          row.moduleFunctionId === this.selectedModuleFunction()) &&
        (this.selectedPersona() == null || row.personaId === this.selectedPersona()),
    ),
  );

  constructor() {
    void this.loadUseCases();
    void this.loadGenerationRuns();
    void this.loadDisplayLookups();
    void this.loadGenerationStatus();
  }

  openEditDialog(row: GeneratedUseCaseAdminRow): void {
    this.editingUseCaseId.set(row.id);
    this.editingTitleDe.set(row.titleDe ?? '');
    this.editingDescriptionDe.set(row.descriptionDe ?? '');
    this.editingDurationMin.set(row.durationMin ?? 1);
    this.editDialogVisible.set(true);
  }

  closeEditDialog(): void {
    this.editDialogVisible.set(false);
    this.editingUseCaseId.set(null);
    this.editingTitleDe.set('');
    this.editingDescriptionDe.set('');
    this.editingDurationMin.set(1);
    this.editSaving.set(false);
    this.generateTitleLoading.set(false);
    this.generateDescriptionLoading.set(false);
  }

  async generateTitleDe(): Promise<void> {
    const current = this.editingTitleDe().trim();
    if (!current) {
      await this.toast.showError('common.error', 'Title must not be empty.');
      return;
    }
    this.generateTitleLoading.set(true);
    try {
      const response = await firstValueFrom(this.useCasesService.improveTitle(current));
      this.editingTitleDe.set(response.text?.trim() ? response.text.trim() : current);
    } catch {
      await this.toast.showError('common.error', 'common.error');
    } finally {
      this.generateTitleLoading.set(false);
    }
  }

  async generateDescriptionDe(): Promise<void> {
    const current = this.editingDescriptionDe().trim();
    if (!current) {
      await this.toast.showError('common.error', 'Description must not be empty.');
      return;
    }
    this.generateDescriptionLoading.set(true);
    try {
      const response = await firstValueFrom(this.useCasesService.improveDescription(current));
      this.editingDescriptionDe.set(response.text?.trim() ? response.text.trim() : current);
    } catch {
      await this.toast.showError('common.error', 'common.error');
    } finally {
      this.generateDescriptionLoading.set(false);
    }
  }

  async saveEditedUseCase(): Promise<void> {
    const useCaseId = this.editingUseCaseId();
    if (!useCaseId) {
      return;
    }
    const request: GeneratedUseCaseUpdateRequest = {
      titleDe: this.editingTitleDe().trim(),
      descriptionDe: this.editingDescriptionDe().trim(),
      durationMin: this.editingDurationMin(),
    };
    if (!request.titleDe || !request.descriptionDe || request.durationMin < 1) {
      await this.toast.showError(
        'common.error',
        'Please provide title, description and duration >= 1.',
      );
      return;
    }

    this.editSaving.set(true);
    try {
      const updated = await firstValueFrom(this.useCasesService.updateUseCase(useCaseId, request));
      this.rows.update((rows) => rows.map((row) => (row.id === updated.id ? updated : row)));
      this.closeEditDialog();
      await this.toast.showSuccess('common.success', 'Use case saved.');
    } catch {
      await this.toast.showError('common.error', 'common.error');
    } finally {
      this.editSaving.set(false);
    }
  }

  async loadUseCases(): Promise<void> {
    this.loading.set(true);
    try {
      const rows = await firstValueFrom(this.useCasesService.loadUseCases());
      this.rows.set(rows ?? []);
    } finally {
      this.loading.set(false);
    }
  }

  async loadGenerationRuns(): Promise<void> {
    this.loading.set(true);
    try {
      const runs = await firstValueFrom(this.useCasesService.loadGenerationRuns());
      this.runRows.set(runs ?? []);
    } finally {
      this.loading.set(false);
    }
  }

  openStartRunDialog(): void {
    this.generationDialogVisible.set(true);
  }

  closeStartRunDialog(): void {
    this.generationDialogVisible.set(false);
  }

  async startNewRunFromDialog(): Promise<void> {
    this.runLoading.set(true);
    try {
      const selectedIds = this.selectedGenerationModuleFunctionIds();
      const runResult = await firstValueFrom(
        this.useCasesService.startGenerationRun({
          moduleFunctionIds: selectedIds.length ? selectedIds : undefined,
        }),
      );
      this.generationRunning.set(true);
      this.generationRunningTooltip.set('A use case generation is currently running.');
      await this.toast.showSuccess(
        'common.success',
        `Run #${runResult.runId} started in background.`,
      );
      this.generationDialogVisible.set(false);
      this.showRunsView.set(true);
      await this.loadGenerationRuns();
    } catch {
      await this.toast.showError('common.error', 'common.error');
    } finally {
      this.runLoading.set(false);
    }
  }

  toggleView(): void {
    const showRuns = !this.showRunsView();
    this.showRunsView.set(showRuns);
    if (showRuns) {
      void this.loadGenerationRuns();
      return;
    }
    void this.loadUseCases();
  }

  getToggleViewLabel(): string {
    return this.showRunsView() ? 'Show Use Cases' : 'Show Runs';
  }

  getAgendaItemLabel(agendaItemId: number): string {
    return this.agendaItemLabels()[agendaItemId] ?? `Agenda ${agendaItemId}`;
  }

  getModuleFunctionLabel(moduleFunctionId: number): string {
    return this.moduleFunctionLabels()[moduleFunctionId] ?? `Function ${moduleFunctionId}`;
  }

  getPersonaLabel(personaId: string): string {
    return this.personaLabels()[personaId] ?? personaId;
  }

  getAttributeLabel(attributeId: string): string {
    return this.attributeLabels()[attributeId] ?? `Attribut ${attributeId}`;
  }

  openAttributeDialog(row: GeneratedUseCaseAdminRow): void {
    const attributeIds = Array.from(new Set(row.subcriteriaAttributeIds ?? []));
    this.selectedAttributeDialogTitle.set(row.titleDe || `Use Case ${row.id}`);
    this.selectedAttributeNames.set(
      attributeIds.map((attributeId) => this.getAttributeLabel(attributeId)),
    );
    this.attributeDialogVisible.set(true);
  }

  closeAttributeDialog(): void {
    this.attributeDialogVisible.set(false);
  }

  openRunDetailsDialog(title: string, items: string[]): void {
    this.runDetailsDialogTitle.set(title);
    this.runDetailsItems.set(items);
    this.runDetailsDialogVisible.set(true);
  }

  closeRunDetailsDialog(): void {
    this.runDetailsDialogVisible.set(false);
    this.runDetailsDialogTitle.set('');
    this.runDetailsItems.set([]);
  }

  getRunUsedModuleFunctionSummary(row: UseCaseGenerationRunAdminRow): string {
    const count = row.usedModuleFunctionIds?.length ?? 0;
    return `${count}`;
  }

  getRunUsedAttributeSummary(row: UseCaseGenerationRunAdminRow): string {
    const count = (row.usedAttributesByModuleFunction ?? []).reduce(
      (sum, group) => sum + (group.attributeIds?.length ?? 0),
      0,
    );
    return `${count}`;
  }

  showUsedModuleFunctions(row: UseCaseGenerationRunAdminRow): void {
    const ids = row.usedModuleFunctionIds ?? [];
    const items = ids.map((id) => `${id} - ${this.getModuleFunctionLabel(id)}`);
    this.openRunDetailsDialog(`Module Functions (Run #${row.id})`, items);
  }

  showUsedAttributes(row: UseCaseGenerationRunAdminRow): void {
    const groups = row.usedAttributesByModuleFunction ?? [];
    const items = groups.flatMap((group) => {
      const moduleFunctionLabel = `${group.moduleFunctionId} - ${this.getModuleFunctionLabel(group.moduleFunctionId)}`;
      const attributeLabels = (group.attributeIds ?? []).map(
        (id) => `  - ${this.getAttributeLabel(id)}`,
      );
      return [moduleFunctionLabel, ...attributeLabels];
    });
    this.openRunDetailsDialog(`Used Attributes (Run #${row.id})`, items);
  }

  isUpdatingEnabled(useCaseId: number): boolean {
    return this.enabledUpdateLoadingById()[useCaseId] ?? false;
  }

  canStartNewRun(): boolean {
    return !this.runLoading() && !this.generationRunning();
  }

  async updateEnabled(useCaseId: number, enabled: boolean): Promise<void> {
    this.enabledUpdateLoadingById.update((state) => ({...state, [useCaseId]: true}));
    try {
      const updated = await firstValueFrom(this.useCasesService.updateEnabled(useCaseId, enabled));
      this.rows.update((rows) =>
        rows.map((row) => (row.id === useCaseId ? {...row, enabled: updated.enabled} : row)),
      );
    } catch {
      await this.toast.showError('common.error', 'common.error');
      await this.loadUseCases();
    } finally {
      this.enabledUpdateLoadingById.update((state) => ({...state, [useCaseId]: false}));
    }
  }

  private async loadDisplayLookups(): Promise<void> {
    const [moduleFunctions, agendaItems, personas, attributes] = await Promise.all([
      firstValueFrom(this.directusService.loadModuleFunctions()),
      firstValueFrom(this.directusService.loadDemonstrationAgenda()),
      firstValueFrom(this.directusService.loadUseCasePersonas()),
      this.directusService.loadSubCriteriaAttributes(),
    ]);

    this.moduleFunctionLabels.set(this.mapModuleFunctionLabels(moduleFunctions ?? []));
    this.agendaItemLabels.set(this.mapAgendaItemLabels(agendaItems ?? []));
    this.personaLabels.set(this.mapPersonaLabels(personas ?? []));
    this.attributeLabels.set(this.mapAttributeLabels(attributes ?? []));

    this.generationModuleFunctions.set(
      (moduleFunctions ?? [])
        .map((moduleFunction: ModuleFunction) => ({
          label: `${moduleFunction.id} - ${this.getModuleFunctionLabel(moduleFunction.id)}`,
          value: moduleFunction.id,
        }))
        .sort((left, right) => left.value - right.value),
    );
  }

  private async loadGenerationStatus(): Promise<void> {
    try {
      const status = await firstValueFrom(this.useCasesService.loadGenerationStatus());
      this.applyGenerationStatus(status);
    } catch {
      this.generationRunning.set(false);
      this.generationRunningTooltip.set('');
    }
  }

  private applyGenerationStatus(status: UseCaseGenerationRunStatusResponse | null): void {
    const running = Boolean(status?.running);
    this.generationRunning.set(running);
    this.generationRunningTooltip.set(running ? 'A use case generation is currently running.' : '');
  }

  private mapModuleFunctionLabels(moduleFunctions: ModuleFunction[]): Record<number, string> {
    const labels: Record<number, string> = {};
    for (const moduleFunction of moduleFunctions) {
      labels[moduleFunction.id] = this.resolveTranslatedName(
        moduleFunction.translations,
        moduleFunction.name ?? `Function ${moduleFunction.id}`,
      );
    }
    return labels;
  }

  private mapAgendaItemLabels(agendaItems: DemonstrationAgenda[]): Record<number, string> {
    const labels: Record<number, string> = {};
    for (const agendaItem of agendaItems) {
      labels[agendaItem.id] = this.resolveTranslatedTopic(agendaItem, `Agenda ${agendaItem.id}`);
    }
    return labels;
  }

  private mapPersonaLabels(personas: UseCasePersona[]): Record<string, string> {
    const labels: Record<string, string> = {};
    for (const persona of personas) {
      labels[persona.id] = this.resolveTranslatedName(persona.translations, persona.id);
    }
    return labels;
  }

  private mapAttributeLabels(attributes: SubcriteriaAttributeDirectus[]): Record<string, string> {
    const labels: Record<string, string> = {};
    for (const attribute of attributes) {
      labels[attribute.id] = this.resolveTranslatedName(
        attribute.translations,
        `Attribut ${attribute.id}`,
      );
    }
    return labels;
  }

  private resolveTranslatedName(
    translations: Array<{language?: string; name?: string}> | undefined,
    fallback: string,
  ): string {
    if (!translations?.length) {
      return fallback;
    }
    const deTranslation = translations.find((translation) => translation.language === 'de');
    if (deTranslation?.name?.trim()) {
      return deTranslation.name;
    }
    if (translations[0]?.name?.trim()) {
      return translations[0].name;
    }
    return fallback;
  }

  private resolveTranslatedTopic(agendaItem: DemonstrationAgenda, fallback: string): string {
    const translations = agendaItem.translations ?? [];
    const deTranslation = translations.find((translation) => translation.language === 'de');
    if (deTranslation?.topic?.trim()) {
      return deTranslation.topic;
    }
    if (translations[0]?.topic?.trim()) {
      return translations[0].topic;
    }
    return fallback;
  }
}
